import { PortalConfig } from '@epilot/customer-portal-client'
import { isPlainObject } from 'lodash'

import {
  installPreviewProxyAdapter,
  removePreviewProxyAdapter
} from 'api/CustomerApiClient'
import {
  installMeteringPreviewProxyAdapter,
  removeMeteringPreviewProxyAdapter
} from 'api/MeteringApiClient'
import { CUSTOMER_QUERY_IDS } from 'api/Queries'
import { DesignJSON } from 'utils/DesignUtils'
import { queryClient } from 'utils/QueryUtils'

import { DUMMY_PORTAL_MESSAGE_EVENT_TYPE } from './events'
import { cleanupPendingRequests, handleProxyResponse } from './proxy-adapter'
import _DUMMY_MOCKS, { removeDynamicMockKeys } from './query-mocks'
import {
  getBuilderOrigin,
  getParentOrigin,
  getReferrerOrigin,
  isPreviewProxyMode,
  setBuilderOrigin,
  setPreviewLoading,
  setPreviewProxyMode
} from './static'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DUMMY_MOCKS = _DUMMY_MOCKS as Record<string, any>

const safeJsonParse = <T>(value: unknown, fallback: T): T => {
  if (typeof value !== 'string') return fallback
  try {
    return JSON.parse(value)
  } catch {
    return fallback
  }
}

const EPILOT_ORIGIN_PATTERN = /\.epilot\.cloud$/

const isValidBuilderOrigin = (origin: string): boolean => {
  const expected = getBuilderOrigin()

  if (expected) {
    try {
      return new URL(origin).origin === new URL(expected).origin
    } catch {
      return false
    }
  }

  // No builder origin captured yet — validate before trusting
  if (window.parent === window) return false

  const referrer = getReferrerOrigin()

  try {
    const incomingOrigin = new URL(origin).origin

    if (referrer && incomingOrigin === new URL(referrer).origin) {
      setBuilderOrigin(origin)

      return true
    }

    // No referrer available — only accept known epilot domains
    if (!referrer && EPILOT_ORIGIN_PATTERN.test(new URL(origin).hostname)) {
      setBuilderOrigin(origin)

      return true
    }
  } catch {
    return false
  }

  return false
}

let currentConfig: PortalConfig

let currentDesign: DesignJSON

/**
 * Regenerate combined multi-schema mock entries for entity lists with multiple
 * configurations (e.g. an entity list showing both "contract" and "meter").
 * The combined mock key (e.g. SEARCH_ENTITIES_contract,meter) is built from
 * the existing per-schema mock data.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function regenerateCombinedMockEntries(pages: any): void {
  if (!Array.isArray(pages)) return

  const combos: string[][] = []

  for (const page of pages) {
    if (!page?.blocks || typeof page.blocks !== 'object') continue

    for (const block of Object.values(page.blocks) as Record<
      string,
      unknown
    >[]) {
      if ((block as { type?: string })?.type !== 'entity_list') continue

      const content = (
        block as { props?: { content?: Record<string, unknown> } }
      )?.props?.content

      if (!content?.configurations || !Array.isArray(content.configurations)) {
        continue
      }

      const schemas = content.configurations
        .map((c: Record<string, unknown>) => c?.schema)
        .filter((s: unknown): s is string => typeof s === 'string')

      if (schemas.length > 1) {
        combos.push(schemas)
      }
    }
  }

  if (combos.length === 0) return

  const generated = new Set<string>()

  for (const combo of combos) {
    const key = combo.join(',')

    if (generated.has(key)) continue
    generated.add(key)

    DUMMY_MOCKS[`${CUSTOMER_QUERY_IDS.SEARCH_ENTITIES}_${key}`] = {
      results: combo.flatMap(
        (s) =>
          DUMMY_MOCKS[`${CUSTOMER_QUERY_IDS.SEARCH_ENTITIES}_${s}`]?.results ||
          []
      )
    }

    DUMMY_MOCKS[`${CUSTOMER_QUERY_IDS.SEARCH_ENTITIES}_GROUPED_${key}`] = {
      groups: combo.flatMap((s) => {
        const grouped =
          DUMMY_MOCKS[`${CUSTOMER_QUERY_IDS.SEARCH_ENTITIES}_GROUPED_${s}`]

        return grouped?.groups || []
      })
    }
  }
}

export const embeddedListener = (event: MessageEvent) => {
  if (!isPlainObject(event.data) || typeof event.data?.type !== 'string') return
  if (!isValidBuilderOrigin(event.origin)) return

  if (
    event.data.type === DUMMY_PORTAL_MESSAGE_EVENT_TYPE.PORTAL_DESIGN_RECEIVED
  ) {
    const design = event.data.noDesignAvailable
      ? currentDesign
      : event.data.design

    window.postMessage(
      { type: DUMMY_PORTAL_MESSAGE_EVENT_TYPE.PORTAL_DESIGN_UPDATE, design },
      window.location.origin
    )

    currentDesign = design
  }
  if (
    event.data.type === DUMMY_PORTAL_MESSAGE_EVENT_TYPE.PORTAL_CONFIG_RECEIVED
  ) {
    const config = event.data.noConfigAvailable
      ? currentConfig
      : typeof event.data.config === 'string'
      ? safeJsonParse(event.data.config, currentConfig)
      : event.data.config

    currentConfig = config

    DUMMY_MOCKS[CUSTOMER_QUERY_IDS.GET_PORTAL_CONFIG] = config
    queryClient.invalidateQueries([CUSTOMER_QUERY_IDS.GET_PORTAL_CONFIG])
  }
  if (event.data.type === DUMMY_PORTAL_MESSAGE_EVENT_TYPE.PORTAL_PAGE_UPDATE) {
    const config =
      typeof event.data.config === 'string'
        ? safeJsonParse(event.data.config, null)
        : event.data.config

    DUMMY_MOCKS[CUSTOMER_QUERY_IDS.GET_PAGES] = config
    // Public pages use the same builder-provided data so they don't fall through
    // to a real API call (which fails in proxy mode due to origin restrictions).
    DUMMY_MOCKS[CUSTOMER_QUERY_IDS.GET_PUBLIC_PAGES] = config

    // Regenerate combined mock entries for multi-schema entity lists
    // (e.g. when user adds a second configuration like "meter" to an entity list)
    regenerateCombinedMockEntries(config)

    queryClient.invalidateQueries([CUSTOMER_QUERY_IDS.GET_PAGES])
    queryClient.invalidateQueries([CUSTOMER_QUERY_IDS.GET_PUBLIC_PAGES])

    // Invalidate all SEARCH_ENTITIES queries so entity lists pick up new combined data.
    // Query keys are ["SEARCH_ENTITIES_contract,meter", ...] so we use predicate matching.
    queryClient.invalidateQueries({
      predicate: (query) =>
        typeof query.queryKey[0] === 'string' &&
        query.queryKey[0].startsWith(CUSTOMER_QUERY_IDS.SEARCH_ENTITIES)
    })

    // Add blockId to URL for highlighting the block in dummy mode only
    if (event.data.blockIds) {
      const currentUrl = new URL(window.location.href)

      currentUrl.searchParams.set('block-ids', event.data.blockIds)

      // Update URL without reload
      window.history.replaceState({}, '', currentUrl.toString())

      // Dispatch custom event to notify about URL change
      window.dispatchEvent(new Event('urlchange'))
    }
  }
  if (
    event.data.type === DUMMY_PORTAL_MESSAGE_EVENT_TYPE.PORTAL_CONFIG_UPDATE
  ) {
    const config = event.data.noConfigAvailable
      ? currentConfig
      : typeof event.data.config === 'string'
      ? safeJsonParse(event.data.config, currentConfig)
      : event.data.config

    currentConfig = config

    DUMMY_MOCKS[CUSTOMER_QUERY_IDS.GET_PORTAL_CONFIG] = config
    queryClient.invalidateQueries([CUSTOMER_QUERY_IDS.GET_PORTAL_CONFIG])
  }
  if (event.data.type === DUMMY_PORTAL_MESSAGE_EVENT_TYPE.BUILDER_DATA_PUSH) {
    const mockId = event.data.mockId

    if (
      typeof mockId !== 'string' ||
      mockId === '__proto__' ||
      mockId === 'constructor'
    )
      return

    DUMMY_MOCKS[mockId] = event.data.mock

    queryClient.invalidateQueries([mockId])
  }

  // Handle loading signal from builder (shown immediately when user selects a contact)
  if (event.data.type === DUMMY_PORTAL_MESSAGE_EVENT_TYPE.PREVIEW_LOADING) {
    setPreviewLoading(!!event.data.loading)
  }

  // Handle preview proxy mode activation/deactivation from builder
  if (event.data.type === DUMMY_PORTAL_MESSAGE_EVENT_TYPE.PREVIEW_PROXY_MODE) {
    const enabled = !!event.data.enabled

    if (enabled && !isPreviewProxyMode()) {
      setPreviewProxyMode(true)
      installPreviewProxyAdapter()
      installMeteringPreviewProxyAdapter()
    } else if (!enabled && isPreviewProxyMode()) {
      setPreviewProxyMode(false)
      removePreviewProxyAdapter()
      removeMeteringPreviewProxyAdapter()
      cleanupPendingRequests()
    }
  }

  // Handle proxied API responses from builder
  if (
    event.data.type === DUMMY_PORTAL_MESSAGE_EVENT_TYPE.PREVIEW_API_RESPONSE
  ) {
    handleProxyResponse(event.data)
  }

  // Handle cache invalidation signal from builder
  if (
    event.data.type === DUMMY_PORTAL_MESSAGE_EVENT_TYPE.PREVIEW_CACHE_INVALIDATE
  ) {
    const patterns = event.data.patterns

    if (Array.isArray(patterns) && patterns.length > 0) {
      patterns.forEach((pattern: string) => {
        queryClient.invalidateQueries({
          predicate: (query) =>
            typeof query.queryKey[0] === 'string' &&
            query.queryKey[0].includes(pattern)
        })
      })
    } else {
      // Invalidate all entity queries (preserve builder-managed keys)
      queryClient.invalidateQueries()
    }
  }

  // Handle preview token clear from builder (page reload or deselecting a customer).
  // Reset entity data mocks to hardcoded defaults but preserve builder-managed keys
  // (config, pages, schemas) so the portal doesn't go blank.
  if (event.data.type === DUMMY_PORTAL_MESSAGE_EVENT_TYPE.PREVIEW_TOKEN_CLEAR) {
    // Clean up proxy mode if active
    if (isPreviewProxyMode()) {
      setPreviewProxyMode(false)
      removePreviewProxyAdapter()
      removeMeteringPreviewProxyAdapter()
      cleanupPendingRequests()
    }

    removeDynamicMockKeys([
      CUSTOMER_QUERY_IDS.GET_PORTAL_CONFIG,
      CUSTOMER_QUERY_IDS.GET_PAGES,
      CUSTOMER_QUERY_IDS.GET_SCHEMAS
    ])

    // Remove all queries from cache. During proxy mode, withDummyMock returned
    // the real queryFn directly — React Query stored these stale references.
    // Invalidating would call them without the proxy adapter, causing 403s.
    // Removing forces components to re-initialize queries with fresh
    // withDummyMock calls, which return mock wrappers since proxy mode is off.
    queryClient.removeQueries()
  }
}

const requestPortalConfig = () => {
  window.parent.postMessage(
    { type: DUMMY_PORTAL_MESSAGE_EVENT_TYPE.GET_PORTAL_CONFIG },
    getParentOrigin()
  )
}

const requestPortalDesign = () => {
  window.parent.postMessage(
    { type: DUMMY_PORTAL_MESSAGE_EVENT_TYPE.GET_PORTAL_DESIGN },
    getParentOrigin()
  )
}

const COMMUNICATION_TIMEOUT = 60 * 1000

export const getConfig = async (
  defaultConfig?: PortalConfig
): Promise<PortalConfig> => {
  return new Promise<PortalConfig>((resolve, reject) => {
    const messageListener = (event: MessageEvent) => {
      if (
        event.data.type === DUMMY_PORTAL_MESSAGE_EVENT_TYPE.PORTAL_CONFIG_UPDATE
      ) {
        cleanup()

        const config = event.data.noConfigAvailable
          ? currentConfig || defaultConfig
          : typeof event.data.config === 'string'
          ? safeJsonParse(event.data.config, currentConfig || defaultConfig)
          : event.data.config

        currentConfig = config

        DUMMY_MOCKS[CUSTOMER_QUERY_IDS.GET_PORTAL_CONFIG] = config
        queryClient.invalidateQueries([CUSTOMER_QUERY_IDS.GET_PORTAL_CONFIG])

        resolve(config)
      }
    }

    const timeout = setTimeout(() => {
      cleanup()

      reject(new Error('Timeout waiting for config'))
    }, COMMUNICATION_TIMEOUT)

    function cleanup() {
      clearTimeout(timeout)
      window.removeEventListener('message', messageListener)
    }

    try {
      window.addEventListener('message', messageListener)

      requestPortalConfig()
    } catch (e) {
      console.error('Error posting message to parent:', e)

      cleanup()

      reject(e)
    }
  })
}

export const getDesign = async (): Promise<void> => {
  return new Promise<void>((resolve, reject) => {
    const messageListener = (event: MessageEvent) => {
      if (
        event.data.type ===
        DUMMY_PORTAL_MESSAGE_EVENT_TYPE.PORTAL_DESIGN_RECEIVED
      ) {
        cleanup()

        const design = event.data.noDesignAvailable
          ? currentDesign
          : event.data.design

        currentDesign = design

        resolve()
      }
    }

    const timeout = setTimeout(() => {
      cleanup()

      reject(new Error('Timeout waiting for design'))
    }, COMMUNICATION_TIMEOUT)

    function cleanup() {
      clearTimeout(timeout)
      window.removeEventListener('message', messageListener)
    }

    try {
      window.addEventListener('message', messageListener)

      requestPortalDesign()
    } catch (e) {
      console.error('Error posting message to parent:', e)

      cleanup()

      reject(e)
    }
  })
}
